import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faSave,
  faChevronDown,
  faChevronRight,
  faRefresh,
  faCopy,
  faTrash,
  faPlus,
  faEdit,
  faGripVertical,
  faLock,
  faLockOpen,
  faToggleOn,
  faToggleOff,
  faEye,
  faEyeSlash,
  faSnowflake,
  faFire,
  faFileImport
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';
import {
  defaultRulesConfig,
  getFieldConstraints,
  createTransferWindow,
  getStageLabel,
  type RuleSection,
  type RuleField,
  type FieldType,
  type FieldValue,
  type MinMaxValue,
  type FieldConstraints,
  type TransferWindow,
  type TransferStage,
  type DependsOn
} from '../config/rulesConfig';

interface ContestRulesData {
  rulesConfig: RuleSection[];
}

// Generate a key from label
const generateKey = (label: string): string => {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
};

const ContestRulesConfig: React.FC = () => {
  const [data, setData] = useState<ContestRulesData>({ rulesConfig: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Template State
  const [templates, setTemplates] = useState<{ _id?: string; id?: string; name: string; createdAt?: string }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTemplateName, setCloneTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Add Section Modal
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');

  // Edit Section Modal
  const [showEditSectionModal, setShowEditSectionModal] = useState(false);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [editingSectionDescription, setEditingSectionDescription] = useState('');
  const [editingSectionOriginalName, setEditingSectionOriginalName] = useState('');

  // Add/Edit Field Modal
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [fieldModalMode, setFieldModalMode] = useState<'add' | 'edit'>('add');
  const [fieldModalSection, setFieldModalSection] = useState('');
  const [fieldModalData, setFieldModalData] = useState<RuleField>({
    key: '',
    label: '',
    desc: '',
    type: 'number',
    default: 0,
    disabled: true,
    required: false,
    locked: false,
    active: true,
    hidden: false,
    frozen: false,
    available: true,
  });
  const [editingFieldOriginalKey, setEditingFieldOriginalKey] = useState('');

  // Notes Modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalSection, setNotesModalSection] = useState('');
  const [notesModalContent, setNotesModalContent] = useState('');

  // Transfer Window Modal State
  const [showTransferWindowModal, setShowTransferWindowModal] = useState(false);
  const [transferWindowModalMode, setTransferWindowModalMode] = useState<'add' | 'edit'>('add');
  const [transferWindowModalSection, setTransferWindowModalSection] = useState('');
  const [transferWindowModalFieldKey, setTransferWindowModalFieldKey] = useState('');
  const [editingTransferWindowId, setEditingTransferWindowId] = useState('');
  const [transferWindowModalData, setTransferWindowModalData] = useState<TransferWindow>(() => createTransferWindow());

  // Reset to empty
  const resetData = useCallback(() => {
    setData({ rulesConfig: [] });
    setExpandedSections(new Set());
    setSelectedTemplateId('');
  }, []);

  // Load default rules config
  const loadDefaultConfig = useCallback(() => {
    const clonedConfig = JSON.parse(JSON.stringify(defaultRulesConfig));
    setData({ rulesConfig: clonedConfig });
    setExpandedSections(new Set(clonedConfig.map((s: RuleSection) => s.section)));
    setSelectedTemplateId('');
    toast.success('Default rules configuration loaded');
  }, []);

  // Load templates list
  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiService.getContestRulesTemplates();
      console.log('Contest Rules Templates list:', response);
      setTemplates(response || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Load a specific template
  const loadTemplate = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplateId('');
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getContestRulesTemplate(templateId);
      console.log('Template response:', response);
      if (response) {
        const rulesConfig = response.rulesConfig || [];
        setData({ rulesConfig });
        setSelectedTemplateId(templateId);
        setExpandedSections(new Set(rulesConfig.map((s: RuleSection) => s.section)));
        toast.success(`Template "${response.name || 'Loaded'}" loaded`);
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      toast.error(`Failed to load template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle save
  const handleSave = () => {
    if (data.rulesConfig.length === 0) {
      toast.error('Add at least one section before saving');
      return;
    }
    if (selectedTemplateId) {
      updateExistingTemplate();
    } else {
      setShowSaveModal(true);
    }
  };

  // Update existing template
  const updateExistingTemplate = async () => {
    try {
      setSaving(true);
      const template = templates.find(t => (t._id || t.id) === selectedTemplateId);
      await apiService.updateContestRulesTemplate(selectedTemplateId, template?.name || 'Unnamed', data);
      toast.success('Template updated successfully');
      loadTemplates();
    } catch (err: any) {
      console.error('Error updating template:', err);
      toast.error(`Failed to update template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Save new template
  const saveNewTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.saveContestRulesTemplate(templateName, data);
      toast.success('Template saved successfully');
      setShowSaveModal(false);
      setTemplateName('');
      loadTemplates();
      const newId = response?._id || response?.id;
      if (newId) {
        setSelectedTemplateId(newId);
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error(`Failed to save template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Clone template
  const handleClone = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const currentTemplate = templates.find(t => (t._id || t.id) === selectedTemplateId);
    setCloneTemplateName(currentTemplate ? `${currentTemplate.name} (Copy)` : 'New Template');
    setShowCloneModal(true);
  };

  const cloneTemplate = async () => {
    if (!cloneTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.saveContestRulesTemplate(cloneTemplateName, data);
      toast.success('Template cloned successfully');
      setShowCloneModal(false);
      setCloneTemplateName('');
      loadTemplates();
      const newId = response?._id || response?.id;
      if (newId) {
        setSelectedTemplateId(newId);
      }
    } catch (err: any) {
      console.error('Error cloning template:', err);
      toast.error(`Failed to clone template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const deleteTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      setSaving(true);
      await apiService.deleteContestRulesTemplate(selectedTemplateId);
      toast.success('Template deleted successfully');
      resetData();
      loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(`Failed to delete template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  // Add Section
  const addSection = () => {
    if (!newSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }
    if (data.rulesConfig.some(s => s.section.toLowerCase() === newSectionName.toLowerCase())) {
      toast.error('Section already exists');
      return;
    }
    setData(prev => ({
      ...prev,
      rulesConfig: [...prev.rulesConfig, {
        section: newSectionName.trim(),
        fields: [],
        notes: [],
        locked: false
      } as RuleSection]
    }));
    setExpandedSections(prev => new Set([...Array.from(prev), newSectionName.trim()]));
    setShowAddSectionModal(false);
    setNewSectionName('');
    setNewSectionDescription('');
    toast.success('Section added');
  };

  // Edit Section
  const openEditSectionModal = (section: RuleSection) => {
    setEditingSectionOriginalName(section.section);
    setEditingSectionName(section.section);
    setEditingSectionDescription('');
    setShowEditSectionModal(true);
  };

  const saveEditSection = () => {
    if (!editingSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }
    if (editingSectionName !== editingSectionOriginalName &&
        data.rulesConfig.some(s => s.section.toLowerCase() === editingSectionName.toLowerCase())) {
      toast.error('Section name already exists');
      return;
    }
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(s =>
        s.section === editingSectionOriginalName
          ? { ...s, section: editingSectionName.trim() }
          : s
      )
    }));
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(editingSectionOriginalName)) {
        next.delete(editingSectionOriginalName);
        next.add(editingSectionName.trim());
      }
      return next;
    });
    setShowEditSectionModal(false);
    toast.success('Section updated');
  };

  // Delete Section
  const deleteSection = (sectionName: string) => {
    if (!window.confirm(`Delete section "${sectionName}" and all its fields?`)) return;
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.filter(s => s.section !== sectionName)
    }));
    toast.success('Section deleted');
  };

  // Toggle Section Locked
  const toggleSectionProperty = (sectionName: string, property: 'locked') => {
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(s =>
        s.section === sectionName ? { ...s, [property]: !s[property] } : s
      )
    }));
  };

  // Toggle Field Locked/Active/Hidden/Frozen
  const toggleFieldProperty = (sectionName: string, fieldKey: string, property: 'locked' | 'active' | 'hidden' | 'frozen') => {
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field =>
            field.key === fieldKey ? { ...field, [property]: !field[property] } : field
          )
        };
      })
    }));
  };

  // Open Add Field Modal
  const openAddFieldModal = (sectionName: string) => {
    setFieldModalMode('add');
    setFieldModalSection(sectionName);
    setFieldModalData({
      key: '',
      label: '',
      desc: '',
      type: 'number',
      default: 0,
      disabled: true,
      required: false,
      locked: false,
      active: true,
      hidden: false,
      frozen: false,
      available: true
    });
    setShowFieldModal(true);
  };

  // Open Edit Field Modal
  const openEditFieldModal = (sectionName: string, field: RuleField) => {
    setFieldModalMode('edit');
    setFieldModalSection(sectionName);
    setEditingFieldOriginalKey(field.key);
    setFieldModalData({ ...field });
    setShowFieldModal(true);
  };

  // Save Field (Add or Edit)
  const saveField = () => {
    if (!fieldModalData.label.trim()) {
      toast.error('Please enter a field label');
      return;
    }

    const key = fieldModalData.key.trim() || generateKey(fieldModalData.label);
    const section = data.rulesConfig.find(s => s.section === fieldModalSection);

    if (fieldModalMode === 'add') {
      if (section?.fields.some(f => f.key === key)) {
        toast.error('Field key already exists in this section');
        return;
      }
    } else {
      if (key !== editingFieldOriginalKey && section?.fields.some(f => f.key === key)) {
        toast.error('Field key already exists in this section');
        return;
      }
    }

    const getDefaultValue = (): FieldValue => {
      switch (fieldModalData.type) {
        case 'boolean': return Boolean(fieldModalData.default);
        case 'string': return String(fieldModalData.default || '');
        case 'select': return String(fieldModalData.default || '');
        case 'minMaxRange': {
          const val = fieldModalData.default as MinMaxValue;
          return { min: val?.min ?? 0, max: val?.max ?? 0 };
        }
        default: return Number(fieldModalData.default) || 0;
      }
    };

    const newField: RuleField = {
      ...fieldModalData,
      key,
      label: fieldModalData.label.trim(),
      default: getDefaultValue(),
      value: getDefaultValue(),
      hidden: fieldModalData.hidden ?? false,
      frozen: fieldModalData.frozen ?? false
    };

    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(s => {
        if (s.section !== fieldModalSection) return s;
        if (fieldModalMode === 'add') {
          return { ...s, fields: [...s.fields, newField] };
        } else {
          return {
            ...s,
            fields: s.fields.map(f => f.key === editingFieldOriginalKey ? newField : f)
          };
        }
      })
    }));

    setShowFieldModal(false);
    toast.success(fieldModalMode === 'add' ? 'Field added' : 'Field updated');
  };

  // Delete Field
  const deleteField = (sectionName: string, fieldKey: string) => {
    if (!window.confirm('Delete this field?')) return;
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(s => {
        if (s.section !== sectionName) return s;
        return { ...s, fields: s.fields.filter(f => f.key !== fieldKey) };
      })
    }));
    toast.success('Field deleted');
  };

  // Update field value
  const updateFieldValue = (sectionName: string, fieldKey: string, value: FieldValue) => {
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.key !== fieldKey) return field;
            return { ...field, value, default: value };
          })
        };
      })
    }));
  };

  // Update minMax field value (min or max separately)
  const updateMinMaxFieldValue = (sectionName: string, fieldKey: string, which: 'min' | 'max', newValue: number) => {
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.key !== fieldKey) return field;
            const currentValue = (field.value ?? field.default) as MinMaxValue;
            const updatedValue: MinMaxValue = {
              min: which === 'min' ? newValue : (currentValue?.min ?? 0),
              max: which === 'max' ? newValue : (currentValue?.max ?? 0)
            };
            return { ...field, value: updatedValue, default: updatedValue };
          })
        };
      })
    }));
  };

  // Transfer Windows CRUD
  const openAddTransferWindowModal = (sectionName: string, fieldKey: string) => {
    setTransferWindowModalMode('add');
    setTransferWindowModalSection(sectionName);
    setTransferWindowModalFieldKey(fieldKey);
    setTransferWindowModalData(createTransferWindow());
    setShowTransferWindowModal(true);
  };

  const openEditTransferWindowModal = (sectionName: string, fieldKey: string, window: TransferWindow) => {
    setTransferWindowModalMode('edit');
    setTransferWindowModalSection(sectionName);
    setTransferWindowModalFieldKey(fieldKey);
    setEditingTransferWindowId(window.id);
    setTransferWindowModalData({ ...window });
    setShowTransferWindowModal(true);
  };

  const saveTransferWindow = () => {
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(section => {
        if (section.section !== transferWindowModalSection) return section;
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.key !== transferWindowModalFieldKey) return field;
            const currentWindows = (field.value as TransferWindow[]) || [];
            let newWindows: TransferWindow[];
            if (transferWindowModalMode === 'add') {
              newWindows = [...currentWindows, transferWindowModalData];
            } else {
              newWindows = currentWindows.map(w =>
                w.id === editingTransferWindowId ? transferWindowModalData : w
              );
            }
            return { ...field, value: newWindows };
          })
        };
      })
    }));
    setShowTransferWindowModal(false);
    toast.success(transferWindowModalMode === 'add' ? 'Transfer window added' : 'Transfer window updated');
  };

  const deleteTransferWindow = (sectionName: string, fieldKey: string, windowId: string) => {
    if (!window.confirm('Delete this transfer window?')) return;
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.key !== fieldKey) return field;
            const currentWindows = (field.value as TransferWindow[]) || [];
            return { ...field, value: currentWindows.filter(w => w.id !== windowId) };
          })
        };
      })
    }));
    toast.success('Transfer window deleted');
  };

  // Notes Modal
  const openNotesModal = (sectionName: string, notes: string[]) => {
    setNotesModalSection(sectionName);
    setNotesModalContent(notes.join('\n'));
    setShowNotesModal(true);
  };

  const saveNotes = () => {
    const notes = notesModalContent.split('\n').filter(n => n.trim());
    setData(prev => ({
      ...prev,
      rulesConfig: prev.rulesConfig.map(s =>
        s.section === notesModalSection ? { ...s, notes } : s
      )
    }));
    setShowNotesModal(false);
    toast.success('Notes updated');
  };

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Render field value input based on type
  const renderFieldInput = (section: RuleSection, field: RuleField) => {
    const value = field.value ?? field.default;

    switch (field.type) {
      case 'boolean':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => updateFieldValue(section.section, field.key, e.target.checked)}
              className="sr-only peer"
              disabled={field.disabled}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        );

      case 'string':
        return (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => updateFieldValue(section.section, field.key, e.target.value)}
            disabled={field.disabled}
            className="w-full max-w-[200px] px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        );

      case 'select':
        return (
          <select
            value={String(value)}
            onChange={(e) => updateFieldValue(section.section, field.key, e.target.value)}
            disabled={field.disabled}
            className="w-full max-w-[200px] px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          >
            {(field.options || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'range': {
        const rangeConstraints = getFieldConstraints(field);
        return (
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={rangeConstraints.min ?? 0}
              max={rangeConstraints.max ?? 100}
              step={rangeConstraints.step ?? 1}
              value={Number(value)}
              onChange={(e) => updateFieldValue(section.section, field.key, parseFloat(e.target.value))}
              disabled={field.disabled}
              className="w-32"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[50px]">
              {value as number}{field.unit ? ` ${field.unit}` : ''}
            </span>
          </div>
        );
      }

      case 'minMaxRange': {
        const minMaxVal = value as MinMaxValue;
        const mmConstraints = getFieldConstraints(field);
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Min:</span>
              <input
                type="number"
                min={mmConstraints.min ?? 0}
                max={mmConstraints.max}
                step={mmConstraints.step ?? 1}
                value={minMaxVal?.min ?? 0}
                onChange={(e) => updateMinMaxFieldValue(section.section, field.key, 'min', parseFloat(e.target.value) || 0)}
                disabled={field.disabled}
                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-center"
              />
            </div>
            <span className="text-gray-400">-</span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Max:</span>
              <input
                type="number"
                min={mmConstraints.min ?? 0}
                max={mmConstraints.max}
                step={mmConstraints.step ?? 1}
                value={minMaxVal?.max ?? 0}
                onChange={(e) => updateMinMaxFieldValue(section.section, field.key, 'max', parseFloat(e.target.value) || 0)}
                disabled={field.disabled}
                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-center"
              />
            </div>
          </div>
        );
      }

      case 'transferWindows': {
        const windows = (value as TransferWindow[]) || [];
        return (
          <div className="w-full">
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => openAddTransferWindowModal(section.section, field.key)}
                disabled={field.disabled}
                className="px-3 py-1.5 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                Add Window
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {windows.length} window{windows.length !== 1 ? 's' : ''} configured
              </span>
            </div>
            {windows.length > 0 && (
              <div className="space-y-2 mt-3">
                {windows.map((tw) => (
                  <div
                    key={tw.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-dark-500 border border-gray-200 dark:border-dark-400 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          {getStageLabel(tw.stage)}
                        </span>
                        {tw.label && (
                          <span className="text-xs text-gray-600 dark:text-gray-400 italic">"{tw.label}"</span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
                        {tw.maxTransfers !== null && <span>Max: {tw.maxTransfers}</span>}
                        {tw.freeTransfers != null && <span>Free: {tw.freeTransfers}</span>}
                        {tw.penaltyPoints != null && tw.penaltyPoints > 0 && <span>Penalty: -{tw.penaltyPoints}pts</span>}
                        {tw.stage === 'custom' && tw.fromMatch && tw.toMatch && (
                          <span>Matches: {tw.fromMatch}-{tw.toMatch}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button
                        onClick={() => openEditTransferWindowModal(section.section, field.key, tw)}
                        disabled={field.disabled}
                        className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded disabled:opacity-50"
                        title="Edit window"
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteTransferWindow(section.section, field.key, tw.id)}
                        disabled={field.disabled}
                        className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                        title="Delete window"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      default: { // number
        const numConstraints = getFieldConstraints(field);
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={numConstraints.min}
              max={numConstraints.max}
              step={numConstraints.step ?? 1}
              value={Number(value)}
              onChange={(e) => updateFieldValue(section.section, field.key, parseFloat(e.target.value) || 0)}
              disabled={field.disabled}
              className="w-full max-w-[120px] px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            />
            {field.unit && (
              <span className="text-sm text-gray-500 dark:text-gray-400">{field.unit}</span>
            )}
          </div>
        );
      }
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-dark-600 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg">
              <FontAwesomeIcon icon={faCog} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contest Rules Config</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Build and manage contest rules templates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selection & Actions */}
      <div className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Template Dropdown */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Load Template
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => loadTemplate(e.target.value)}
              disabled={loadingTemplates || loading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">-- Select Template --</option>
              {templates.map((template) => (
                <option key={template._id || template.id} value={template._id || template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-2 flex-wrap">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} />
              {selectedTemplateId ? 'Update' : 'Save'}
            </button>
            <button
              onClick={handleClone}
              disabled={!selectedTemplateId || saving || loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCopy} />
              Clone
            </button>
            <button
              onClick={deleteTemplate}
              disabled={!selectedTemplateId || saving || loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete
            </button>
            <button
              onClick={resetData}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRefresh} />
              Clear
            </button>
            <button
              onClick={loadDefaultConfig}
              disabled={loading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              title="Load default rules configuration"
            >
              <FontAwesomeIcon icon={faFileImport} />
              Load Default
            </button>
          </div>
        </div>
      </div>

      {/* Add Section Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowAddSectionModal(true)}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Section
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && data.rulesConfig.length === 0 && (
        <div className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-12 text-center">
          <FontAwesomeIcon icon={faCog} className="text-gray-300 dark:text-gray-600 text-5xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Sections Yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Start building your contest rules by adding a section.</p>
          <button
            onClick={() => setShowAddSectionModal(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add First Section
          </button>
        </div>
      )}

      {/* Contest Rules Sections */}
      {!loading && data.rulesConfig.length > 0 && (
        <div className="space-y-4">
          {data.rulesConfig.map((section) => (
            <div
              key={section.section}
              className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 overflow-hidden"
            >
              {/* Section Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-dark-400">
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1"
                  onClick={() => toggleSection(section.section)}
                >
                  <FontAwesomeIcon icon={faGripVertical} className="text-gray-400 cursor-grab" />
                  <FontAwesomeIcon
                    icon={expandedSections.has(section.section) ? faChevronDown : faChevronRight}
                    className="text-gray-500 dark:text-gray-400 w-4"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{section.section}</h3>
                    {section.dependsOn && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">Depends on: {section.dependsOn.key} = {String(section.dependsOn.value)}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-dark-600 px-2 py-0.5 rounded-full ml-2">
                    {section.fields.length} fields
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Locked Toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSectionProperty(section.section, 'locked'); }}
                    className={`p-1.5 rounded transition-colors ${section.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title={section.locked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
                  >
                    <FontAwesomeIcon icon={section.locked ? faLock : faLockOpen} className="w-4 h-4" />
                  </button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-dark-300 mx-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); openNotesModal(section.section, section.notes || []); }}
                    className="p-1.5 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded"
                    title="Edit notes"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditSectionModal(section); }}
                    className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                    title="Edit section"
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSection(section.section); }}
                    className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="Delete section"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Section Content */}
              {expandedSections.has(section.section) && (
                <div className="p-4">
                  {/* Notes */}
                  {(section.notes?.length ?? 0) > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                      <ul className="text-sm text-amber-800 dark:text-amber-300 list-disc list-inside">
                        {(section.notes || []).map((note, idx) => (
                          <li key={idx}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Add Field Button */}
                  <div className="mb-4">
                    <button
                      onClick={() => openAddFieldModal(section.section)}
                      className="px-3 py-1.5 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-lg flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-xs" />
                      Add Field
                    </button>
                  </div>

                  {/* Fields */}
                  {section.fields.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No fields yet. Add your first field.</p>
                  ) : (
                    <div className="space-y-3">
                      {section.fields.map((field) => {
                        const constraints = getFieldConstraints(field);
                        return (
                        <div
                          key={field.key}
                          className={`p-4 bg-gray-50 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-400 ${!field.active ? 'opacity-50' : ''} ${field.locked ? 'border-l-4 border-l-red-500' : ''} ${field.hidden ? 'border-l-4 border-l-gray-400' : ''} ${field.frozen ? 'border-r-4 border-r-cyan-500' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              {/* Field Info */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Label</p>
                                <p className="font-medium text-gray-900 dark:text-white">{field.label}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Key: {field.key}</p>
                                {field.desc && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{field.desc}</p>
                                )}
                              </div>

                              {/* Type Info */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Type</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    field.type === 'boolean' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                                    field.type === 'string' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                    field.type === 'select' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                                    field.type === 'minMaxRange' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' :
                                    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                  }`}>
                                    {field.type === 'minMaxRange' ? 'Min/Max' : field.type}
                                  </span>
                                  {field.required && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Required</span>
                                  )}
                                  {field.disabled && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400">Disabled</span>
                                  )}
                                  {field.hidden && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-gray-400 dark:bg-gray-600 text-white">Hidden</span>
                                  )}
                                  {field.frozen && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400">Frozen</span>
                                  )}
                                </div>
                              </div>

                              {/* Constraints */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Constraints</p>
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  {field.type === 'number' || field.type === 'range' ? (
                                    <>
                                      {constraints.min !== undefined && <span>Min: {constraints.min} </span>}
                                      {constraints.max !== undefined && <span>Max: {constraints.max} </span>}
                                      {constraints.step !== undefined && <span>Step: {constraints.step}</span>}
                                      {constraints.min === undefined && constraints.max === undefined && <span className="text-gray-400">None</span>}
                                    </>
                                  ) : field.type === 'select' ? (
                                    <span>{field.options?.length || 0} options</span>
                                  ) : field.type === 'minMaxRange' ? (
                                    <>
                                      <span className="text-pink-600 dark:text-pink-400">
                                        {(field.value as MinMaxValue)?.min ?? 0} - {(field.value as MinMaxValue)?.max ?? 0}
                                      </span>
                                      {constraints.min !== undefined && constraints.max !== undefined && (
                                        <span className="text-xs text-gray-400 ml-2">
                                          (Range: {constraints.min}-{constraints.max})
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-gray-400">N/A</span>
                                  )}
                                </div>
                              </div>

                              {/* Value Input */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Value</p>
                                {renderFieldInput(section, field)}
                              </div>
                            </div>

                            {/* Field Actions */}
                            <div className="flex items-center gap-1">
                              {/* Active Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'active')}
                                className={`p-1.5 rounded transition-colors ${field.active ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                              >
                                <FontAwesomeIcon icon={field.active ? faToggleOn : faToggleOff} className="w-4 h-4" />
                              </button>
                              {/* Locked Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'locked')}
                                className={`p-1.5 rounded transition-colors ${field.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.locked ? 'Locked (Paywall) - Click to unlock' : 'Unlocked - Click to lock'}
                              >
                                <FontAwesomeIcon icon={field.locked ? faLock : faLockOpen} className="w-3.5 h-3.5" />
                              </button>
                              {/* Hidden Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'hidden')}
                                className={`p-1.5 rounded transition-colors ${field.hidden ? 'text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.hidden ? 'Hidden in UI - Click to show' : 'Visible in UI - Click to hide'}
                              >
                                <FontAwesomeIcon icon={field.hidden ? faEyeSlash : faEye} className="w-3.5 h-3.5" />
                              </button>
                              {/* Frozen Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'frozen')}
                                className={`p-1.5 rounded transition-colors ${field.frozen ? 'text-cyan-600 hover:bg-cyan-100 dark:hover:bg-cyan-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.frozen ? 'Frozen (read-only after publish) - Click to unfreeze' : 'Editable after publish - Click to freeze'}
                              >
                                <FontAwesomeIcon icon={field.frozen ? faSnowflake : faFire} className="w-3.5 h-3.5" />
                              </button>
                              <div className="w-px h-4 bg-gray-300 dark:bg-dark-300 mx-0.5" />
                              <button
                                onClick={() => openEditFieldModal(section.section, field)}
                                className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                                title="Edit field"
                              >
                                <FontAwesomeIcon icon={faEdit} className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteField(section.section, field.key)}
                                className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                title="Delete field"
                              >
                                <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Team Settings, Entry Rules"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && addSection()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="Brief description of this section"
                  value={newSectionDescription}
                  onChange={(e) => setNewSectionDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddSectionModal(false); setNewSectionName(''); setNewSectionDescription(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={addSection}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Section Modal */}
      {showEditSectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section Name *</label>
                <input
                  type="text"
                  value={editingSectionName}
                  onChange={(e) => setEditingSectionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && saveEditSection()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  value={editingSectionDescription}
                  onChange={(e) => setEditingSectionDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditSectionModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveEditSection}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Field Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {fieldModalMode === 'add' ? 'Add Field' : 'Edit Field'}
            </h3>

            <div className="space-y-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Team Size, Entry Fee"
                  value={fieldModalData.label}
                  onChange={(e) => setFieldModalData(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key <span className="text-gray-400 text-xs">(auto-generated if empty)</span>
                </label>
                <input
                  type="text"
                  placeholder={generateKey(fieldModalData.label) || 'e.g., team_size'}
                  value={fieldModalData.key}
                  onChange={(e) => setFieldModalData(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={fieldModalData.type}
                  onChange={(e) => {
                    const newType = e.target.value as FieldType;
                    setFieldModalData(prev => ({
                      ...prev,
                      type: newType,
                      default: newType === 'boolean' ? false :
                               newType === 'string' || newType === 'select' ? '' :
                               newType === 'minMaxRange' ? { min: 0, max: 0 } : 0
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="number">Number</option>
                  <option value="boolean">Boolean (Yes/No)</option>
                  <option value="string">Text</option>
                  <option value="select">Select (Dropdown)</option>
                  <option value="range">Range (Slider)</option>
                  <option value="minMaxRange">Min/Max Range</option>
                </select>
              </div>

              {/* Options for select type */}
              {fieldModalData.type === 'select' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Options <span className="text-gray-400 text-xs">(one per line: value|label)</span>
                  </label>
                  <textarea
                    value={(fieldModalData.options || []).map(o => `${o.value}|${o.label}`).join('\n')}
                    onChange={(e) => {
                      const options = e.target.value.split('\n').filter(l => l.trim()).map(line => {
                        const [value, label] = line.split('|');
                        return { value: value?.trim() || '', label: label?.trim() || value?.trim() || '' };
                      });
                      setFieldModalData(prev => ({ ...prev, options }));
                    }}
                    rows={4}
                    placeholder="small|Small&#10;medium|Medium&#10;large|Large"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              )}

              {/* Min/Max/Step for number/range */}
              {(fieldModalData.type === 'number' || fieldModalData.type === 'range') && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min</label>
                    <input
                      type="number"
                      value={fieldModalData.min ?? ''}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, min: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max</label>
                    <input
                      type="number"
                      value={fieldModalData.max ?? ''}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, max: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Step</label>
                    <input
                      type="number"
                      value={fieldModalData.step ?? ''}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, step: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Unit */}
              {(fieldModalData.type === 'number' || fieldModalData.type === 'range') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit</label>
                  <input
                    type="text"
                    placeholder="e.g., players, ₹, %"
                    value={fieldModalData.unit || ''}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, unit: e.target.value || undefined }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Default Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Value</label>
                {fieldModalData.type === 'boolean' ? (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(fieldModalData.default)}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, default: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                    <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                      {fieldModalData.default ? 'Yes' : 'No'}
                    </span>
                  </label>
                ) : fieldModalData.type === 'select' ? (
                  <select
                    value={String(fieldModalData.default)}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, default: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">-- Select default --</option>
                    {(fieldModalData.options || []).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : fieldModalData.type === 'string' ? (
                  <input
                    type="text"
                    value={String(fieldModalData.default)}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, default: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : fieldModalData.type === 'minMaxRange' ? (
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min</label>
                      <input
                        type="number"
                        min={fieldModalData.min ?? 0}
                        step={fieldModalData.step ?? 1}
                        value={(fieldModalData.default as MinMaxValue)?.min ?? 0}
                        onChange={(e) => setFieldModalData(prev => ({
                          ...prev,
                          default: { ...(prev.default as MinMaxValue), min: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Max</label>
                      <input
                        type="number"
                        min={fieldModalData.min ?? 0}
                        step={fieldModalData.step ?? 1}
                        value={(fieldModalData.default as MinMaxValue)?.max ?? 0}
                        onChange={(e) => setFieldModalData(prev => ({
                          ...prev,
                          default: { ...(prev.default as MinMaxValue), max: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                ) : (
                  <input
                    type="number"
                    step={fieldModalData.step ?? 1}
                    value={Number(fieldModalData.default)}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, default: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                )}
              </div>

              {/* Help Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Help Text</label>
                <input
                  type="text"
                  placeholder="Description shown to users"
                  value={fieldModalData.desc || ''}
                  onChange={(e) => setFieldModalData(prev => ({ ...prev, desc: e.target.value || '' }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Flags */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Field Properties</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.required}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, required: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.disabled}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, disabled: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Disabled</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.active}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, active: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.locked}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, locked: e.target.checked }))}
                      className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Locked (Paywall)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.hidden ?? false}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, hidden: e.target.checked }))}
                      className="w-4 h-4 text-gray-600 bg-gray-100 border-gray-300 rounded focus:ring-gray-500 dark:focus:ring-gray-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Hidden</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldModalData.frozen ?? false}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, frozen: e.target.checked }))}
                      className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Frozen (After Publish)</span>
                  </label>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
                  <p><strong>Locked:</strong> Requires addon purchase to unlock (paywall)</p>
                  <p><strong>Hidden:</strong> Not shown in UI at all</p>
                  <p><strong>Frozen:</strong> Cannot edit after contest starts/publishes</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowFieldModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveField}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
              >
                {fieldModalMode === 'add' ? 'Add Field' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Window Modal */}
      {showTransferWindowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {transferWindowModalMode === 'add' ? 'Add Transfer Window' : 'Edit Transfer Window'}
            </h3>

            <div className="space-y-4">
              {/* Stage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stage</label>
                <select
                  value={transferWindowModalData.stage}
                  onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, stage: e.target.value as TransferStage }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="league">League Stage</option>
                  <option value="mid_season">Mid Season</option>
                  <option value="playoffs">Playoffs</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Custom Range (only shown for custom stage) */}
              {transferWindowModalData.stage === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Match</label>
                    <input
                      type="number"
                      min={1}
                      value={transferWindowModalData.fromMatch ?? ''}
                      onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, fromMatch: parseInt(e.target.value) || undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Match</label>
                    <input
                      type="number"
                      min={1}
                      value={transferWindowModalData.toMatch ?? ''}
                      onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, toMatch: parseInt(e.target.value) || undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="10"
                    />
                  </div>
                </div>
              )}

              {/* Custom Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  value={transferWindowModalData.label ?? ''}
                  onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, label: e.target.value || undefined }))}
                  placeholder="e.g., Mid-season break"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Transfer Limits */}
              {transferWindowModalData.stage && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Transfers <span className="text-gray-400 text-xs">(empty = unlimited)</span>
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={transferWindowModalData.maxTransfers ?? ''}
                        onChange={(e) => setTransferWindowModalData(prev => ({
                          ...prev,
                          maxTransfers: e.target.value === '' ? null : parseInt(e.target.value)
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Free Transfers</label>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        value={transferWindowModalData.freeTransfers ?? ''}
                        onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, freeTransfers: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Penalty Points <span className="text-gray-400 text-xs">(per extra transfer)</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={transferWindowModalData.penaltyPoints ?? ''}
                      onChange={(e) => setTransferWindowModalData(prev => ({ ...prev, penaltyPoints: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowTransferWindowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveTransferWindow}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
              >
                {transferWindowModalMode === 'add' ? 'Add Window' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-lg shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Notes - {notesModalSection}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">One note per line</p>
            <textarea
              value={notesModalContent}
              onChange={(e) => setNotesModalContent(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Enter notes, one per line..."
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveNotes}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Save Template</h3>
            <input
              type="text"
              placeholder="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveNewTemplate()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowSaveModal(false); setTemplateName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveNewTemplate}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clone Template</h3>
            <input
              type="text"
              placeholder="New template name"
              value={cloneTemplateName}
              onChange={(e) => setCloneTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && cloneTemplate()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowCloneModal(false); setCloneTemplateName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={cloneTemplate}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Cloning...' : 'Clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JSON Preview (collapsible) */}
      <details className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 mt-6">
        <summary className="p-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors rounded-xl">
          JSON Preview
        </summary>
        <pre className="p-4 bg-gray-900 text-green-400 text-xs overflow-auto max-h-96 rounded-b-xl">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default ContestRulesConfig;
